import { useGesture } from '@use-gesture/react';
import { useEffect, useLayoutEffect, useState, useRef } from 'react';

import { fitBetween } from '../utils/fitBetween.js';

export interface RelativePosition {
  top: number;
  right: number;
  left?: number;
}

/**
 * Add the drag and drop logic into elementRef.
 *
 * @param toolbarElement - Ref of the element container, that will be dragged
 * @param maxBounds - max bounds of the draggable element
 * @param initialPosition - initial position of the draggable element
 */
export function useDragElement(
  toolbarElement: HTMLDivElement | undefined,
  maxBounds: RelativePosition,
  initialPosition: RelativePosition,
) {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  /** Element reference that will trigger all useGesture logic */
  const triggerDragRef = useRef<HTMLButtonElement>(null);

  /** Current relative top and right position of the element */
  const [currentPosition, setCurrentPosition] =
    useState<RelativePosition>(initialPosition);
  /** Last offset between the initial position of the element and the position after the last drag event */
  const [lastIdlePosition, setLastIdlePosition] =
    useState<RelativePosition>(initialPosition);

  useLayoutEffect(() => {
    // if bounds change, meaning the bounds element or the draggable element change it's size, it should readjust the position
    setCurrentPosition((prevCurrentPosition) => {
      const right = fitBetween(prevCurrentPosition.right, 0, maxBounds.right);
      const top = fitBetween(prevCurrentPosition.top, 0, maxBounds.top);

      return { right, top, left: prevCurrentPosition.left || 0 };
    });

    setLastIdlePosition((prevIdlePosition) => {
      const right = fitBetween(prevIdlePosition.right, 0, maxBounds.right);
      const top = fitBetween(prevIdlePosition.top, 0, maxBounds.top);
      const left = fitBetween(
        prevIdlePosition.left || 0,
        0,
        maxBounds.left || 0,
      );

      return { right, top, left };
    });
  }, [maxBounds]);

  useEffect(() => {
    const isCurrentLeftPositionDefined = currentPosition.left !== undefined;
    const isInitialLeftPositionDefined = initialPosition.left !== undefined;
    const isLeftPositionDifferent =
      currentPosition.left !== initialPosition.left;
    const bothLeftPositionsAreZero =
      initialPosition.left === 0 && currentPosition.left === 0;

    // Don't update the current position if the toolbar was dragged from the initial left side position and then resized. APPDEV-12778.
    if (
      isInitialLeftPositionDefined &&
      isCurrentLeftPositionDefined &&
      (!bothLeftPositionsAreZero || isLeftPositionDifferent)
    ) {
      return;
    }

    setCurrentPosition(initialPosition);
    setLastIdlePosition(initialPosition);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- It's only worth checking if the position coordinates changed, not the object
  }, [initialPosition.top, initialPosition.right]);

  useEffect(() => {
    const { top, right } = currentPosition;

    if (toolbarElement) {
      toolbarElement.style.right = `${right}px`;
      toolbarElement.style.top = `${top}px`;
    }
  }, [toolbarElement, currentPosition]);

  useGesture(
    {
      onDrag: (event) => {
        /**
         * negative movedX - drag left
         * positive movedX - drag right
         * negative movedY - drag top
         * positive movedY - drag bottom
         */
        const [movedX, movedY] = event.movement;

        const x0 = lastIdlePosition.right + movedX;
        const x = lastIdlePosition.right - movedX;
        const y = lastIdlePosition.top + movedY;

        setIsDragging(true);

        const right = fitBetween(x, 0, maxBounds.right);
        const top = fitBetween(y, 0, maxBounds.top);
        const left = fitBetween(
          x0,
          0,
          maxBounds.right - (toolbarElement?.clientWidth || 0),
        );

        setCurrentPosition({ right, top, left });
      },
      onDragEnd: () => {
        setLastIdlePosition(currentPosition);
        setIsDragging(false);
      },
    },
    {
      target: triggerDragRef,
      eventOptions: { passive: false },
      drag: { filterTaps: true },
    },
  );

  return { isDragging, triggerDragRef };
}
